import {Component, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {distinctUntilChanged, map, startWith, Subject, takeUntil} from 'rxjs';
import {ProposalTileSortParameter} from '../../enum/proposal-tile-sort-parameter';
import {FilterRole} from '../../enum/filter-role';
import {QuestionOptions} from '../../constants/question-options';
import {Store} from '@ngrx/store';
import {
  selectAllTilesLoaded,
  selectCurrentFilter, selectError,
  selectLoadingTiles,
  selectProposalNumber,
  selectProposals,
  selectProposalSelected,
  selectProposalSelectedId
} from '../../store/proposal/selectors/proposal.selectors';
import {ProposalAppState} from '../../store/proposal/reducers/proposal.reducer';
import {
  ProposalNumberActions,
  ProposalOverview,
  ProposalSelectedActions,
  ProposalTileActions
} from '../../store/proposal/actions/proposal.actions';
import {FormBuilder, FormControl} from '@angular/forms';
import {ProposalFilterQuery} from '../../model/proposal-filter-query';
import {LocalStorageItem} from '../../enum/local-storage-item';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {selectUser, selectUserId, selectUserLoading} from '../../store/user/selectors/user.selectors';
import { HttpErrorResponse } from '@angular/common/http';
import {CustomSnackbarComponent} from '../../components/snackbar/custom-snackbar/custom-snackbar.component';
import {NavigationStart, Router} from '@angular/router';
import {NgScrollbar} from 'ngx-scrollbar';
import {MatSnackBar, MatSnackBarRef} from '@angular/material/snack-bar';
import {withLatestFrom} from 'rxjs/operators';
import {Roles} from '../../enum/roles';

@Component({
    selector: 'app-proposal-overview',
    templateUrl: './proposal-overview.component.html',
    styleUrls: ['./proposal-overview.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ProposalOverviewComponent implements OnInit {

  @ViewChild('scrollProposalTiles') scrollProposalTiles: NgScrollbar;

  ProposalTileSortParameter = ProposalTileSortParameter;

  iconsPath = {};

  QuestionOptions = QuestionOptions;
  proposalTiles$ = this.proposalStore.select(selectProposals);
  proposalNumber$ = this.proposalStore.select(selectProposalNumber);

  proposalSelected$ = this.proposalStore.select(selectProposalSelected);
  proposalSelectedId$ = this.proposalStore.select(selectProposalSelectedId);

  loading$ = this.proposalStore.select(selectLoadingTiles);
  loadingUser$ = this.userStore.select(selectUserLoading);

  error$ = this.proposalStore.select(selectError);
  errorOccurred = false;
  allTilesLoaded$ = this.proposalStore.select(selectAllTilesLoaded);
  filterSelected$ = this.proposalStore.select(selectCurrentFilter);
  destroy$ = new Subject<void>();


  activeUserId$ = this.userStore.select(selectUserId);
  selectedUser$ = this.userStore.select(selectUser);

  filterHidden: boolean = false;

  private snackbarRef: MatSnackBarRef<CustomSnackbarComponent> | null = null;

  formGroup = this.fb.group({
    sortBy: new FormControl<any>(ProposalTileSortParameter.CREATION_DATE),
    ascending: true,
  });

  constructor(
    public proposalStore: Store<ProposalAppState>,
    public userStore: Store<UserAppState>,
    public fb: FormBuilder,
    private readonly snackbar: MatSnackBar,
    private readonly router: Router
  ) {
    this.iconsPath[FilterRole.FAVORITES] = 'starEmpty.svg';
    this.iconsPath[FilterRole.PRIVATE] = 'lock.svg';
    this.iconsPath[FilterRole.OFFER_SUBMITTED] = 'bidSubmitted-colorless.svg';
    this.iconsPath[FilterRole.HAS_OFFERS] = 'bidSubmitted-colorless.svg';
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && this.snackbarRef) {
        this.snackbarRef.dismiss();
      }
    });
  }

  ngAfterViewInit(): void {
    this.scrollProposalTiles.scrolled.pipe(
      distinctUntilChanged(),
      withLatestFrom(this.loading$, this.allTilesLoaded$)
    ).subscribe(([_, loading, allTilesLoaded]) => {
      if (!loading && !allTilesLoaded) {
        this.loadNext();
      }
    });
  }


  ngOnInit() {
    this.error$.subscribe((error: HttpErrorResponse) => {
      if (error.status === 500) {
        this.errorOccurred = true;
        this.snackbarRef = this.snackbar.openFromComponent(CustomSnackbarComponent, {
          panelClass: ['custom-snackbar'],
          duration: 0
        });
      }
    });
    this.proposalStore.dispatch(ProposalNumberActions.loadProposalNumber());

    this.proposalTiles$.subscribe(proposals => {
    });


    const storedSortBy = localStorage.getItem(LocalStorageItem.SORT_BY);
    const storedAscending = localStorage.getItem(LocalStorageItem.ASCENDING);
    if (storedSortBy) {
      this.formGroup.controls.sortBy.setValue(storedSortBy);
    }
    if (storedAscending) {
      this.formGroup.controls.ascending.setValue(storedAscending === 'true');
    }

    this.formGroup.valueChanges
      .pipe(
        startWith(null),
        map(() => ({
          sortBy: this.formGroup.value.sortBy,
          ascending: this.formGroup.value.ascending
        })),
        distinctUntilChanged(),
        takeUntil(this.destroy$))
      .subscribe(({sortBy, ascending}) => {
        this.proposalStore.dispatch(ProposalOverview.updateFilter({
          filter: {
            sortBy: sortBy,
            ascending: ascending
          }
        }));
        localStorage.setItem(LocalStorageItem.SORT_BY, this.formGroup.value.sortBy);
        localStorage.setItem(LocalStorageItem.ASCENDING, this.formGroup.value.ascending.toString());
      });
    this.proposalStore.dispatch(ProposalOverview.resetPagination());
    this.proposalStore.dispatch(ProposalSelectedActions.deselect());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeSortDirection() {
    this.formGroup.controls.ascending.setValue(
      !this.formGroup.value.ascending
    );
  }

  backToTop() {
    this.scrollProposalTiles.scrollTo({
      top: 0
    });
  }

  selectProposalTile(proposalId: string) {
    this.proposalStore.dispatch(ProposalSelectedActions.load({proposalId: proposalId}));
  }

  loadNext() {
    this.proposalStore.dispatch(ProposalTileActions.load());
  }

  isDefaultFilter(selectedFilter: ProposalFilterQuery): boolean {
    const defaultFilter = new ProposalFilterQuery();

    return (
      selectedFilter.selectedRole === defaultFilter.selectedRole &&
      selectedFilter.noCoverage === defaultFilter.noCoverage &&
      selectedFilter.leadInsurerNeeded === defaultFilter.leadInsurerNeeded &&
      selectedFilter.followingInsurerNeeded === defaultFilter.followingInsurerNeeded &&
      selectedFilter.minimumAmountInsured === defaultFilter.minimumAmountInsured &&
      selectedFilter.maximumAmountInsured === defaultFilter.maximumAmountInsured &&
      selectedFilter.includeOwn === true &&
      selectedFilter.onlySubstitutes === false &&
      selectedFilter.page === 0 &&
      selectedFilter.pageSize === defaultFilter.pageSize
    );
  }

  protected readonly FilterRole = FilterRole;
  protected readonly Roles = Roles;
}
